import { useState, useEffect, useCallback } from "react";
import {
  LayoutList, MessagesSquare, Target, Wrench, Trophy, HelpCircle,
  Pin, Heart, MessageCircle, Image as ImageIcon, PenLine,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Lucide category icons in HG green (audit #10 — no emoji chips).
const FORUM_CATEGORIES = [
  { id: "all", name: "All Posts", Icon: LayoutList },
  { id: "general", name: "General Discussion", Icon: MessagesSquare },
  { id: "fitting-tips", name: "Fitting Tips & Techniques", Icon: Target },
  { id: "equipment", name: "Equipment Talk", Icon: Wrench },
  { id: "success-stories", name: "Success Stories", Icon: Trophy },
  { id: "questions", name: "Questions & Help", Icon: HelpCircle },
];

export function CoachRoundTable({ user }) {
  const [posts, setPosts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [showNewPost, setShowNewPost] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/forum/posts?page=${page}`;
      if (selectedCategory !== "all") url += `&category=${selectedCategory}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      
      const res = await fetch(url, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setPosts(data.posts);
        setTotalPages(data.pages);
      }
    } catch (err) {
      console.error("Failed to load posts:", err);
    }
    setLoading(false);
  }, [selectedCategory, searchQuery, page]);

  const loadStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/forum/stats`, { credentials: "include" });
      if (res.ok) {
        setStats(await res.json());
      }
    } catch (err) {
      console.error("Failed to load stats:", err);
    }
  }, []);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  function formatDate(dateStr) {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    
    return date.toLocaleDateString();
  }

  if (selectedPost) {
    return (
      <PostDetail 
        post={selectedPost} 
        user={user}
        onBack={() => { setSelectedPost(null); loadPosts(); }}
      />
    );
  }

  if (showNewPost) {
    return (
      <NewPostForm 
        onClose={() => setShowNewPost(false)}
        onSuccess={() => { setShowNewPost(false); loadPosts(); }}
      />
    );
  }

  return (
    <div className="content forum-page" data-testid="coach-round-table">
      <div className="section-title">Coach Round Table</div>
      <p className="section-sub">Connect with fellow fitters, share experiences, and learn from each other.</p>

      {/* Stats Bar */}
      {stats && (
        <div className="forum-stats-bar">
          <div className="forum-stat">
            <span className="stat-num">{stats.total_posts}</span>
            <span className="stat-label">Posts</span>
          </div>
          <div className="forum-stat">
            <span className="stat-num">{stats.total_replies}</span>
            <span className="stat-label">Replies</span>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="forum-controls">
        <div className="forum-search">
          <input
            type="text"
            placeholder="Search posts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadPosts()}
            data-testid="forum-search"
          />
          <button className="btn btn-secondary" onClick={loadPosts}>Search</button>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => setShowNewPost(true)}
          data-testid="new-post-btn"
        >
          + New Post
        </button>
      </div>

      {/* Categories */}
      <div className="forum-categories">
        {FORUM_CATEGORIES.map(cat => (
          <button
            key={cat.id}
            className={`category-btn ${selectedCategory === cat.id ? "active" : ""}`}
            onClick={() => { setSelectedCategory(cat.id); setPage(1); }}
            data-testid={`category-${cat.id}`}
          >
            <span className="cat-icon"><cat.Icon size={15} strokeWidth={1.8} /></span>
            {cat.name}
            {stats?.category_counts?.[cat.id] && (
              <span className="cat-count">{stats.category_counts[cat.id]}</span>
            )}
          </button>
        ))}
      </div>

      {/* Posts List */}
      <div className="forum-posts">
        {loading ? (
          <div className="loading-state">Loading posts...</div>
        ) : posts.length === 0 ? (
          <div className="empty-state-hg" data-testid="empty-posts">
            <MessagesSquare size={28} strokeWidth={1.5} />
            <p>No posts yet — the Round Table is where fitters trade what works.</p>
            <button className="btn btn-primary" onClick={() => setShowNewPost(true)}><PenLine size={14} strokeWidth={1.8} /> Start the first discussion</button>
          </div>
        ) : (
          posts.map(post => (
            <div 
              key={post.post_id} 
              className={`forum-post-card ${post.is_pinned ? "pinned" : ""}`}
              onClick={() => setSelectedPost(post)}
              data-testid={`post-${post.post_id}`}
            >
              {post.is_pinned && <span className="pinned-badge"><Pin size={11} strokeWidth={1.8} /> Pinned</span>}
              <div className="post-header">
                <span className="post-category">
                  {(() => { const C = FORUM_CATEGORIES.find(c => c.id === post.category)?.Icon; return C ? <C size={15} strokeWidth={1.8} /> : null; })()}
                </span>
                <h3 className="post-title">{post.title}</h3>
              </div>
              <p className="post-preview">
                {post.content.substring(0, 150)}
                {post.content.length > 150 && "..."}
              </p>
              <div className="post-meta">
                <span className="post-author">
                  {post.author_name}
                  {post.author_company && ` · ${post.author_company}`}
                </span>
                <span className="post-date">{formatDate(post.created_at)}</span>
                <span className="post-stats">
                  <Heart size={12} strokeWidth={1.8} /> {post.like_count} · <MessageCircle size={12} strokeWidth={1.8} /> {post.reply_count}
                </span>
              </div>
              {post.images?.length > 0 && (
                <span className="has-images"><ImageIcon size={12} strokeWidth={1.8} /> {post.images.length} image(s)</span>
              )}
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="forum-pagination">
          <button 
            className="btn btn-secondary"
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
          >
            Previous
          </button>
          <span>Page {page} of {totalPages}</span>
          <button 
            className="btn btn-secondary"
            disabled={page === totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

function NewPostForm({ onClose, onSuccess }) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("general");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/forum/posts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ title, content, category, images: [] })
      });
      
      if (res.ok) {
        onSuccess();
      }
    } catch (err) {
      console.error("Failed to create post:", err);
    }
    setSubmitting(false);
  }

  return (
    <div className="content new-post-form" data-testid="new-post-form">
      <div className="section-title">Create New Post</div>
      <button className="btn btn-secondary back-btn" onClick={onClose}>← Back</button>

      <form onSubmit={handleSubmit} className="post-form">
        <div className="form-field">
          <label>Category</label>
          <select 
            value={category} 
            onChange={(e) => setCategory(e.target.value)}
            data-testid="post-category"
          >
            {FORUM_CATEGORIES.filter(c => c.id !== "all").map(cat => (
              <option key={cat.id} value={cat.id}>
                {cat.icon} {cat.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label>Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What's on your mind?"
            required
            data-testid="post-title"
          />
        </div>

        <div className="form-field">
          <label>Content</label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Share your thoughts, questions, or experiences..."
            rows={8}
            required
            data-testid="post-content"
          />
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={submitting || !title.trim() || !content.trim()}
            data-testid="submit-post-btn"
          >
            {submitting ? "Posting..." : "Post Discussion"}
          </button>
        </div>
      </form>
    </div>
  );
}

function PostDetail({ post, user, onBack }) {
  const [replies, setReplies] = useState([]);
  const [replyContent, setReplyContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(post.like_count);
  const [loading, setLoading] = useState(true);

  // Same shape as App's loadAdminData: useCallback so the effect can name it.
  const loadPost = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/forum/posts/${post.post_id}`, { 
        credentials: "include" 
      });
      if (res.ok) {
        const data = await res.json();
        setReplies(data.replies);
      }
    } catch (err) {
      console.error("Failed to load post:", err);
    }
    setLoading(false);
  }, [post.post_id]);

  useEffect(() => {
    loadPost();
  }, [loadPost]);

  async function handleLike() {
    try {
      const res = await fetch(`${API_URL}/api/forum/posts/${post.post_id}/like`, {
        method: "POST",
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setLiked(data.liked);
        setLikeCount(data.like_count);
      }
    } catch (err) {
      console.error("Failed to like post:", err);
    }
  }

  async function handleReply(e) {
    e.preventDefault();
    if (!replyContent.trim()) return;

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/forum/posts/${post.post_id}/replies`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content: replyContent, images: [] })
      });
      
      if (res.ok) {
        setReplyContent("");
        loadPost();
      }
    } catch (err) {
      console.error("Failed to post reply:", err);
    }
    setSubmitting(false);
  }

  async function handleDeleteReply(replyId) {
    if (!window.confirm("Delete this reply?")) return;
    
    try {
      const res = await fetch(`${API_URL}/api/forum/replies/${replyId}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (res.ok) {
        loadPost();
      }
    } catch (err) {
      console.error("Failed to delete reply:", err);
    }
  }

  async function handleHidePost() {
    try {
      const res = await fetch(`${API_URL}/api/admin/forum/posts/${post.post_id}/visibility`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ hidden: true })
      });
      if (res.ok) {
        onBack();
      }
    } catch (err) {
      console.error("Failed to hide post:", err);
    }
  }

  function formatDate(dateStr) {
    return new Date(dateStr).toLocaleString();
  }

  return (
    <div className="content post-detail" data-testid="post-detail">
      <button className="btn btn-secondary back-btn" onClick={onBack}>← Back to Posts</button>

      <div className="post-full">
        <div className="post-header">
          <span className="post-category-badge">
            {FORUM_CATEGORIES.find(c => c.id === post.category)?.icon}
            {FORUM_CATEGORIES.find(c => c.id === post.category)?.name}
          </span>
          <h2>{post.title}</h2>
        </div>

        <div className="post-author-info">
          <div className="author-avatar">{post.author_name.charAt(0)}</div>
          <div>
            <div className="author-name">{post.author_name}</div>
            {post.author_company && <div className="author-company">{post.author_company}</div>}
          </div>
          <div className="post-timestamp">{formatDate(post.created_at)}</div>
        </div>

        <div className="post-content">
          {post.content.split('\n').map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>

        {post.images?.length > 0 && (
          <div className="post-images">
            {post.images.map((img, i) => (
              <img key={i} src={img} alt={`Post image ${i + 1}`} />
            ))}
          </div>
        )}

        <div className="post-actions">
          <button 
            className={`action-btn ${liked ? "liked" : ""}`}
            onClick={handleLike}
            data-testid="like-btn"
          >
            <Heart size={13} strokeWidth={1.8} /> {likeCount}
          </button>
          <span className="reply-count"><MessageCircle size={13} strokeWidth={1.8} /> {replies.length} replies</span>
          
          {user?.is_admin && (
            <button 
              className="action-btn admin-action"
              onClick={handleHidePost}
              data-testid="hide-post-btn"
            >
              Hide Post
            </button>
          )}
        </div>
      </div>

      {/* Reply Form */}
      <div className="reply-form-section">
        <h3>Add a Reply</h3>
        <form onSubmit={handleReply}>
          <textarea
            value={replyContent}
            onChange={(e) => setReplyContent(e.target.value)}
            placeholder="Share your thoughts..."
            rows={3}
            data-testid="reply-input"
          />
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={submitting || !replyContent.trim()}
            data-testid="submit-reply-btn"
          >
            {submitting ? "Posting..." : "Post Reply"}
          </button>
        </form>
      </div>

      {/* Replies */}
      <div className="replies-section">
        <h3>Replies ({replies.length})</h3>
        {loading ? (
          <div className="loading-state">Loading replies...</div>
        ) : replies.length === 0 ? (
          <div className="empty-state">No replies yet. Be the first to respond!</div>
        ) : (
          replies.map(reply => (
            <div key={reply.reply_id} className="reply-card" data-testid={`reply-${reply.reply_id}`}>
              <div className="reply-header">
                <div className="reply-avatar">{reply.author_name.charAt(0)}</div>
                <div className="reply-author">{reply.author_name}</div>
                <div className="reply-date">{formatDate(reply.created_at)}</div>
              </div>
              <div className="reply-content">
                {reply.content.split('\n').map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
              {(reply.author_id === user?.user_id || user?.is_admin) && (
                <button 
                  className="btn btn-text btn-danger"
                  onClick={() => handleDeleteReply(reply.reply_id)}
                >
                  Delete
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default CoachRoundTable;
